import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, IconButton, Typography } from '@mui/material';
import { CallEndIcon, KeypadIcon, PauseIcon, PlayIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import {
  hangupCallAction,
  holdCallAction,
  sendDtmfAction,
  unholdCallAction,
} from 'store/system/actions';
import { selectCallState } from 'store/system/selectors';

import {
  callerInfoStyles,
  callerNameStyles,
  containerStyles,
  controlButtonActiveStyles,
  controlButtonStyles,
  controlRowStyles,
  dtmfButtonStyles,
  dtmfGridStyles,
  hangupButtonStyles,
  timerStyles,
} from './ActiveCall.styles';
import { ActiveCallTestIds } from './ActiveCall.testIds';

const DTMF_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
const ONE_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const HOLD_DEBOUNCE_MS = 250;

const formatDuration = seconds => {
  const mins = Math.floor(seconds / SECONDS_PER_MINUTE);
  const secs = seconds % SECONDS_PER_MINUTE;

  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const DtmfButton = memo(({ digit, onPress }) => {
  const handleClick = useCallback(() => {
    onPress(digit);
  }, [digit, onPress]);

  return (
    <IconButton
      data-testid={ActiveCallTestIds.DTMF}
      key={digit}
      sx={dtmfButtonStyles}
      onClick={handleClick}
    >
      {digit}
    </IconButton>
  );
});

DtmfButton.displayName = 'DtmfButton';

DtmfButton.propTypes = {
  digit: PropTypes.string.isRequired,
  onPress: PropTypes.func.isRequired,
};

const ActiveCall = () => {
  const dispatch = useDispatch();
  const callState = useSelector(selectCallState);
  const [elapsed, setElapsed] = useState(0);
  const [showDtmf, setShowDtmf] = useState(false);
  const isHolding = callState.isOnHold;
  const [holdPending, setHoldPending] = useState(false);
  const holdTimerRef = useRef(null);

  // Clean up hold debounce timer on unmount
  useEffect(
    () => () => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (callState.startTime) {
        setElapsed(Math.floor((Date.now() - callState.startTime) / ONE_SECOND));
      }
    }, ONE_SECOND);

    return () => clearInterval(interval);
  }, [callState.startTime]);

  const onHangup = useCallback(() => {
    dispatch(hangupCallAction());
  }, [dispatch]);

  const onToggleHold = useCallback(() => {
    if (holdPending) {
      return;
    }

    setHoldPending(true);

    if (isHolding) {
      dispatch(unholdCallAction());
    }

    if (!isHolding) {
      dispatch(holdCallAction());
    }

    // Reset pending flag after 250ms to prevent rapid toggles
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      setHoldPending(false);
    }, HOLD_DEBOUNCE_MS);
  }, [dispatch, isHolding, holdPending]);

  const onToggleDtmf = useCallback(() => {
    setShowDtmf(prev => !prev);
  }, []);

  const onDtmfPress = useCallback(
    digit => {
      dispatch(sendDtmfAction(digit));
    },
    [dispatch],
  );

  const displayName = callState.callerName || callState.dialedNumber || 'Unknown';

  const holdButtonStyles = useMemo(
    () => (isHolding ? controlButtonActiveStyles : controlButtonStyles),
    [isHolding],
  );

  const dtmfToggleStyles = useMemo(
    () => (showDtmf ? controlButtonActiveStyles : controlButtonStyles),
    [showDtmf],
  );

  return (
    <Box sx={containerStyles}>
      <Box sx={callerInfoStyles}>
        <Typography sx={callerNameStyles} variant="bold20">
          {displayName}
        </Typography>
      </Box>

      <Typography sx={timerStyles} variant="regular14">
        {formatDuration(elapsed)}
      </Typography>

      {showDtmf && (
        <Box sx={dtmfGridStyles}>
          {DTMF_KEYS.map(digit => (
            <DtmfButton digit={digit} key={digit} onPress={onDtmfPress} />
          ))}
        </Box>
      )}

      <Box sx={controlRowStyles}>
        <IconButton
          aria-label={isHolding ? 'Resume' : 'Hold'}
          data-testid={ActiveCallTestIds.HOLD}
          disabled={holdPending}
          sx={holdButtonStyles}
          onClick={onToggleHold}
        >
          {isHolding ? <PlayIcon /> : <PauseIcon />}
        </IconButton>

        <IconButton
          aria-label="Hang up"
          data-testid={ActiveCallTestIds.HANGUP}
          sx={hangupButtonStyles}
          onClick={onHangup}
        >
          <CallEndIcon />
        </IconButton>

        <IconButton
          aria-label="Keypad"
          data-testid={ActiveCallTestIds.KEYPAD}
          sx={dtmfToggleStyles}
          onClick={onToggleDtmf}
        >
          <KeypadIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default memo(ActiveCall);
